import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import type { ResolvedNode } from '../../../../vendor/agentplace-a2ui/walker.ts';
import { messages } from '../localization/messages.ts';
import { cn } from '../utils/cn.ts';
import { useSurfaceAction } from './surface-context.ts';
import {
  contentExplorationContext,
  EXPLORE_CONTENT_ACTION,
  isContentExplorationClick,
} from './content-exploration.ts';

export function useContentExploration(
  node: ResolvedNode | undefined,
  selection: Record<string, unknown>,
  surrounding: Record<string, unknown> = {},
): (() => void) | null {
  const dispatch = useSurfaceAction();
  if (!dispatch || !node) {
    return null;
  }
  return () =>
    dispatch(EXPLORE_CONTENT_ACTION, contentExplorationContext(node, selection, surrounding));
}

/**
 * An explorable object with one pointer and keyboard target covering its visible bounds.
 * Put the card/row border, background, radius and padding on this root via className,
 * rather than nesting it inside a padded card. Nested controls remain independent.
 */
export function ExploreContent({
  node,
  selection,
  surrounding,
  subject,
  children,
  className,
}: {
  node: ResolvedNode;
  selection: Record<string, unknown>;
  surrounding?: Record<string, unknown>;
  subject: string;
  children: ReactNode;
  className?: string;
}) {
  const intl = useIntl();
  const explore = useContentExploration(node, selection, surrounding);
  if (!explore) {
    return <div className={className}>{children}</div>;
  }
  return (
    // The native button supplies keyboard activation; the region extends its pointer target.
    <div
      className={cn(
        'relative cursor-pointer rounded-md transition-colors hover:bg-muted/60 hover:outline hover:outline-1 hover:outline-primary/40 focus-within:bg-muted/60',
        className,
      )}
      onClick={(event) => {
        if (!event.defaultPrevented && isContentExplorationClick(event.target)) {
          event.stopPropagation();
          explore();
        }
      }}
    >
      <button
        type="button"
        aria-label={intl.formatMessage(messages.tellMeMoreAbout, { subject })}
        className="pointer-events-none absolute inset-0 rounded-[inherit] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        onClick={(event) => {
          event.stopPropagation();
          explore();
        }}
      />
      {children}
    </div>
  );
}
